import { AssetState, DamageType } from "__graphql__/types";
import { useAnalytics } from "shared/hooks/useAnalytics";

import { useDamageReportMutation, useUpdateAssetStateMutation } from "../queries/assets.generated";
import { useAssetManagementStore } from "../stores/assetManagementStore";
import { ASSET_TYPE, DAMAGE_TYPE_OPTIONS } from "../utils/constants";

/**
 * Reports a battery whose barcode is damaged, the same way the non-functional
 * "No QR code / no barcode" reason does in a battery count: a damage report, then the
 * state set to defect.
 */
export function useReportMissingBarcode() {
  const { sendSegmentTrackEvent } = useAnalytics();
  const { currentTaskId, countSessionId } = useAssetManagementStore();
  const [createDamageReport, { loading: isCreatingDamageReport }] = useDamageReportMutation();
  const [updateAssetState, { loading: isUpdatingAssetState }] = useUpdateAssetStateMutation();

  const reportMissingBarcode = async (assetId: string) => {
    await createDamageReport({
      variables: {
        input: {
          assetId,
          type: DamageType.damage_type_identifier_missing,
          description:
            DAMAGE_TYPE_OPTIONS.find(
              (option) => option.key === DamageType.damage_type_identifier_missing,
            )?.label ?? null,
        },
      },
    });
    sendSegmentTrackEvent("assetDamagedReported", {
      asset_type: ASSET_TYPE,
      asset_count_process_id: currentTaskId!,
      asset_count_session_id: countSessionId!,
      flow_type: "battery_count",
      asset_id: assetId,
      asset_state: AssetState.asset_state_defect,
      reason: DamageType.damage_type_identifier_missing,
    });
    await updateAssetState({
      variables: { input: { assetId, newState: AssetState.asset_state_defect } },
    });
  };

  return {
    reportMissingBarcode,
    isReportingMissingBarcode: isCreatingDamageReport || isUpdatingAssetState,
  };
}
